import { useState } from 'react'
import { isRole, roleHasCapability } from '@qim/shared'
import { ApiError, setAccountSettings } from '../api.js'
import { formatInstant } from '../format.js'
import type { AccountSettingMark, AccountSettingsWire } from '../api.js'
import type { JSX } from 'react'
import type { TenantKind } from '@qim/shared'

/**
 * What this cloud account is, and what it will accept (IM-127).
 *
 * ## The block is a control-plane refusal and it is **not a control**
 *
 * Said on the screen, in as many words, because a switch that looks like a
 * barrier and is not is worse than no switch: somebody will rely on it. The
 * grant in the customer's own account still permits a run for as long as it has
 * left, and anyone holding the deploy role can run Terraform by hand. What this
 * stops is *this console and this API* starting one — which is worth having,
 * because it is the path every instance actually gets created by.
 *
 * That is the grant's own honesty in reverse: there, the customer's cloud
 * enforces it and the code merely explains; here, the code enforces it and the
 * cloud does not.
 *
 * ## The state is shown to everyone; the switch is offered to a role that holds
 * the capability
 *
 * Whether an account is Qrvey's own is a fact anyone reading the screen needs —
 * it is what makes a long-lived instance *interesting* rather than alarming. So
 * the state renders from the account's own read, which `account:read` already
 * returns, and only the control is behind `account:settings`.
 *
 * Not a permission check: the API refuses regardless and records the decision
 * either way. Offering somebody a switch that always fails is a worse way to
 * tell them no than not offering it.
 */

const canChange = (role: string): boolean =>
  isRole(role) && roleHasCapability(role, 'account:settings')

/** Who decided a setting and when, or nothing when nobody has. */
const Attribution = ({ mark }: { readonly mark?: AccountSettingMark }): JSX.Element | null =>
  mark === undefined ? null : (
    <span className="muted">
      {mark.value ? 'On' : 'Off'} since <time dateTime={mark.at}>{formatInstant(mark.at)}</time>,
      set by {mark.by}
    </span>
  )

export const AccountSettingsPanel = ({
  baseUrl,
  tenantId,
  accountKey,
  settings = {},
  kind,
  role,
  onChanged,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly accountKey: string
  /**
   * Absent means nobody has changed a setting, and reads as `{}`.
   *
   * It used to mean "an API that predates settings" and render nothing, which
   * was true of no API still deployed and false of every account nobody had
   * touched: the listing omitted the empty map, so the Settings tab was blank
   * on all of them (IM-173). The API sends `{}` now; this is the second half.
   */
  readonly settings?: AccountSettingsWire
  /** The customer's kind, which decides whether this account is Qrvey's own. */
  readonly kind?: TenantKind | undefined
  readonly role: string
  readonly onChanged: () => void
}): JSX.Element => {
  const [busy, setBusy] = useState<string | undefined>(undefined)
  const [problem, setProblem] = useState<string | undefined>(undefined)

  const blocked = settings.newInstancesBlocked?.value === true
  const editable = canChange(role)

  const change = async (setting: 'newInstancesBlocked', value: boolean): Promise<void> => {
    setBusy(setting)
    setProblem(undefined)
    try {
      await setAccountSettings(baseUrl, tenantId, accountKey, { [setting]: value })
      // Re-read rather than splicing the new value into local state: what the
      // account is, is the API's answer — the arrangement every other write in
      // this console uses.
      onChanged()
    } catch (error: unknown) {
      /*
       * A refusal and a failure are different things to be told (IM-144).
       *
       * Every `ApiError` read as "your role may not permit it", which for four
       * days was wrong about every attempt: the write itself was refused by
       * DynamoDB and answered 500, and the panel told people to look at their
       * permissions. A guess about the cause is worse than the plain fact when
       * it points somewhere there is nothing to find.
       */
      setProblem(
        error instanceof ApiError && error.status === 403
          ? 'That change was refused. Your role does not permit it.'
          : error instanceof ApiError
            ? `That change did not save (${String(error.status)}). It is not your permissions — try again, and if it repeats it is worth reporting.`
            : 'Could not save that just now.',
      )
    } finally {
      setBusy(undefined)
    }
  }

  return (
    <section className="panel">
      <h2>Account settings</h2>

      {/*
        Whose account this is, read-only. It was a switch here until IM-149
        made it the customer's kind, fixed when the customer is created — and a
        tab with the switch simply gone reads as a missing feature (IM-173), so
        it says where the answer now lives.
      */}
      <p>
        <strong>
          {kind === 'qrvey'
            ? 'Qrvey’s own account.'
            : kind === 'customer'
              ? 'A customer’s account.'
              : 'Whose account this is could not be read.'}
        </strong>{' '}
        <span className="hint">
          Decided by the customer it belongs to, when that customer was created, and not changed
          here.
        </span>
      </p>

      <div className="field">
        <label htmlFor="settingBlocked">
          <input
            id="settingBlocked"
            type="checkbox"
            checked={blocked}
            disabled={!editable || busy !== undefined}
            onChange={(event) => {
              void change('newInstancesBlocked', event.target.checked)
            }}
            aria-describedby="settingBlocked-hint"
          />{' '}
          Take no new instances
        </label>
        <span id="settingBlocked-hint" className="hint">
          Refuses an apply naming a deployment id this account has no runs for. A plan, a destroy,
          and an apply or upgrade against an instance that already exists are unaffected.
        </span>
        <Attribution
          {...(settings.newInstancesBlocked === undefined
            ? {}
            : { mark: settings.newInstancesBlocked })}
        />
      </div>

      {/*
        The honesty this screen exists to carry. Rendered whether or not the
        setting is on, because somebody deciding whether to turn it on is
        exactly who needs to know what it will and will not do.
      */}
      <p className="hint">
        <strong>This is not a control.</strong> It refuses new instances from this console and this
        API. The grant in the customer&rsquo;s own account still permits a run for as long as it has
        left, and anyone holding the deploy role can run Terraform by hand.
      </p>

      {!editable ? (
        <p className="hint">Your role does not permit changing these (account:settings).</p>
      ) : null}

      {problem === undefined ? null : (
        <p role="alert" className="failure">
          {problem}
        </p>
      )}
    </section>
  )
}

/**
 * Marks one of Qrvey's own customers, wherever it is named beside real ones.
 *
 * Driven by the customer's kind since IM-149. It used to be IM-127's
 * per-account `internal` setting, which is why it still sits beside account
 * names on some screens: every account of a Qrvey customer is Qrvey's.
 */
export const QrveyBadge = ({
  kind,
}: {
  readonly kind: TenantKind | undefined
}): JSX.Element | null =>
  kind === 'qrvey' ? (
    // A word, not only a colour — the rule the whole console follows, and this
    // one gets read on a projector beside real customers' accounts. Not
    // `.env-chip`: that carries the accent and exists to say, unmissably, which
    // environment this console is. This is a quiet fact about one customer, and
    // borrowing that chip would spend the alarm on something that is not one.
    <span className="internal-chip">Qrvey</span>
  ) : null
